import { memo, useState, useEffect } from 'react';
import { Handle, Position, NodeProps, useReactFlow, useNodes } from 'reactflow';
import { Calendar, Edit2, Trash2, Clock, User, Sparkles, Timer } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Input, Label } from '@caruso/ui';
import { useQuery } from '@tanstack/react-query';
import { usersService } from '@/services/users.service';

interface ScheduleCheckData {
  scheduleUserId?: string;
  schedulePeriodType?: 'next_day' | 'next_days';
  schedulePeriodDays?: number;
  scheduleDuration?: number;
  scheduleIncludeWeekends?: boolean;
  scheduleMinTime?: string;
  scheduleMaxTime?: string;
  // Novos campos
  useHumanizedMessage?: boolean;
  timeoutMinutes?: number;
  timeoutNodeId?: string;
}

export const ScheduleCheckNode = memo(({ data, id }: NodeProps<ScheduleCheckData>) => {
  const { deleteElements, setNodes } = useReactFlow();
  const allNodes = useNodes();
  const [isEditing, setIsEditing] = useState(false);
  const [scheduleUserId, setScheduleUserId] = useState(data.scheduleUserId || '');
  const [schedulePeriodType, setSchedulePeriodType] = useState<'next_day' | 'next_days'>(data.schedulePeriodType || 'next_days');
  const [schedulePeriodDays, setSchedulePeriodDays] = useState(data.schedulePeriodDays || 7);
  const [scheduleDuration, setScheduleDuration] = useState(data.scheduleDuration || 60);
  const [scheduleIncludeWeekends, setScheduleIncludeWeekends] = useState(data.scheduleIncludeWeekends ?? false);
  const [scheduleMinTime, setScheduleMinTime] = useState(data.scheduleMinTime || '09:00');
  const [scheduleMaxTime, setScheduleMaxTime] = useState(data.scheduleMaxTime || '18:00');
  // Novos estados
  const [useHumanizedMessage, setUseHumanizedMessage] = useState(data.useHumanizedMessage ?? true);
  const [timeoutMinutes, setTimeoutMinutes] = useState(data.timeoutMinutes || 60);
  const [timeoutNodeId, setTimeoutNodeId] = useState(data.timeoutNodeId || '');

  // Filtrar nós disponíveis para timeout (exceto o atual e nós start)
  const availableNodes = allNodes.filter(
    (node) => node.id !== id && node.type !== 'start'
  );

  // Load users
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.getAll(),
  });

  const users = usersData || [];

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const handleSave = () => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              scheduleUserId,
              schedulePeriodType,
              schedulePeriodDays,
              scheduleDuration,
              scheduleIncludeWeekends,
              scheduleMinTime,
              scheduleMaxTime,
              useHumanizedMessage,
              timeoutMinutes,
              timeoutNodeId,
            },
          };
        }
        return node;
      })
    );
    setIsEditing(false);
  };

  const selectedUser = users.find(u => u.id === scheduleUserId);

  return (
    <>
      <div className="bg-purple-500/10 border-2 border-purple-500 rounded-lg p-4 min-w-[280px] shadow-lg">
        <Handle type="target" position={Position.Top} className="!bg-purple-500" />

        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-purple-600">Consulta de Agendamento</div>
              <div className="text-xs text-gray-500">Busca horários disponíveis</div>
            </div>
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 hover:bg-purple-100 rounded transition-colors"
              title="Editar"
            >
              <Edit2 className="w-4 h-4 text-purple-600" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1.5 hover:bg-red-100 rounded transition-colors"
              title="Excluir"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-purple-500" />
            <span className="font-medium text-gray-700">
              {selectedUser ? selectedUser.name : 'Nenhum usuário selecionado'}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-purple-500" />
            <span className="text-gray-600">
              {scheduleDuration} min • {scheduleMinTime} às {scheduleMaxTime}
            </span>
          </div>

          <div className="mt-2 p-2 bg-white/50 rounded text-xs text-gray-600 border border-purple-200">
            <div className="flex justify-between">
              <span>Período:</span>
              <span className="font-medium">
                {schedulePeriodType === 'next_day' ? 'Próximo dia' : `Próximos ${schedulePeriodDays} dias`}
              </span>
            </div>
            <div className="flex justify-between mt-1">
              <span>Finais de semana:</span>
              <span className="font-medium">{scheduleIncludeWeekends ? 'Sim' : 'Não'}</span>
            </div>
            {useHumanizedMessage && (
              <div className="flex items-center gap-1 mt-1 text-purple-600">
                <Sparkles className="w-3 h-3" />
                <span className="font-medium">Mensagem humanizada com IA</span>
              </div>
            )}
            <div className="flex justify-between mt-1">
              <span className="flex items-center gap-1">
                <Timer className="w-3 h-3" />
                Timeout:
              </span>
              <span className="font-medium">{timeoutMinutes} min</span>
            </div>
          </div>

          {!scheduleUserId && (
            <div className="text-xs text-purple-600 italic">
              ⚠️ Configure o usuário para consulta
            </div>
          )}
        </div>

        <Handle type="source" position={Position.Bottom} className="!bg-purple-500" />
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-brand-ink border-brand-gold/20 text-brand-ivory max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-brand-gold flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Configurar Consulta de Agendamento
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/60">
              Configure os parâmetros para busca de horários disponíveis na agenda
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-brand-ivory/80">
                Usuário <span className="text-red-400">*</span>
              </Label>
              <select
                value={scheduleUserId}
                onChange={(e) => setScheduleUserId(e.target.value)}
                className="w-full mt-1 bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory rounded px-3 py-2"
              >
                <option value="">Selecione o usuário...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-brand-ivory/40 mt-1">
                Usuário cuja agenda será consultada
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-brand-ivory/80">
                  Tipo de Período <span className="text-red-400">*</span>
                </Label>
                <select
                  value={schedulePeriodType}
                  onChange={(e) => setSchedulePeriodType(e.target.value as 'next_day' | 'next_days')}
                  className="w-full mt-1 bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory rounded px-3 py-2"
                >
                  <option value="next_day">Próximo dia</option>
                  <option value="next_days">Próximos X dias</option>
                </select>
              </div>

              {schedulePeriodType === 'next_days' && (
                <div>
                  <Label className="text-brand-ivory/80">
                    Quantidade de Dias <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max="90"
                    value={schedulePeriodDays}
                    onChange={(e) => setSchedulePeriodDays(parseInt(e.target.value) || 7)}
                    className="bg-brand-ink/50 border-brand-gold/20 text-brand-ivory mt-1"
                  />
                </div>
              )}
            </div>

            <div>
              <Label className="text-brand-ivory/80">
                Duração do Agendamento <span className="text-red-400">*</span>
              </Label>
              <select
                value={scheduleDuration.toString()}
                onChange={(e) => setScheduleDuration(parseInt(e.target.value))}
                className="w-full mt-1 bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory rounded px-3 py-2"
              >
                <option value="30">30 minutos</option>
                <option value="60">1 hora</option>
                <option value="120">2 horas</option>
                <option value="240">4 horas</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-brand-ivory/80">
                  Hora Mínima <span className="text-red-400">*</span>
                </Label>
                <Input
                  type="time"
                  value={scheduleMinTime}
                  onChange={(e) => setScheduleMinTime(e.target.value)}
                  className="bg-brand-ink/50 border-brand-gold/20 text-brand-ivory mt-1"
                />
                <p className="text-xs text-brand-ivory/40 mt-1">
                  Horário mínimo para agendamento
                </p>
              </div>

              <div>
                <Label className="text-brand-ivory/80">
                  Hora Máxima <span className="text-red-400">*</span>
                </Label>
                <Input
                  type="time"
                  value={scheduleMaxTime}
                  onChange={(e) => setScheduleMaxTime(e.target.value)}
                  className="bg-brand-ink/50 border-brand-gold/20 text-brand-ivory mt-1"
                />
                <p className="text-xs text-brand-ivory/40 mt-1">
                  Horário máximo para agendamento
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="include-weekends"
                checked={scheduleIncludeWeekends}
                onChange={(e) => setScheduleIncludeWeekends(e.target.checked)}
                className="w-4 h-4 rounded border-brand-gold/20 bg-brand-ink/50 text-brand-gold focus:ring-brand-gold focus:ring-offset-brand-ink"
              />
              <Label htmlFor="include-weekends" className="text-brand-ivory/80 cursor-pointer">
                Incluir finais de semana
              </Label>
            </div>

            {/* Mensagem Humanizada com IA */}
            <div className="mt-4 p-3 bg-purple-500/10 rounded border border-purple-500/30">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <Label className="text-brand-ivory/80 font-medium">
                  Mensagem Humanizada com IA
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="use-humanized"
                  checked={useHumanizedMessage}
                  onChange={(e) => setUseHumanizedMessage(e.target.checked)}
                  className="w-4 h-4 rounded border-brand-gold/20 bg-brand-ink/50 text-purple-500 focus:ring-purple-500 focus:ring-offset-brand-ink"
                />
                <Label htmlFor="use-humanized" className="text-brand-ivory/80 cursor-pointer text-sm">
                  Usar ChatGPT para gerar mensagem humanizada
                </Label>
              </div>
              <p className="text-xs text-brand-ivory/40 mt-2">
                Quando ativado, o ChatGPT gera mensagens como: "O que você acha de amanhã às 14h?" ao invés de listas formatadas.
              </p>
            </div>

            {/* Configuração de Timeout */}
            <div className="mt-4 p-3 bg-orange-500/10 rounded border border-orange-500/30">
              <div className="flex items-center gap-2 mb-2">
                <Timer className="w-4 h-4 text-orange-400" />
                <Label className="text-brand-ivory/80 font-medium">
                  Configuração de Timeout
                </Label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-brand-ivory/80 text-sm">Tempo de Espera</Label>
                  <select
                    value={timeoutMinutes.toString()}
                    onChange={(e) => setTimeoutMinutes(parseInt(e.target.value))}
                    className="w-full mt-1 bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory rounded px-3 py-2 text-sm"
                  >
                    <option value="15">15 minutos</option>
                    <option value="30">30 minutos</option>
                    <option value="60">1 hora</option>
                    <option value="120">2 horas</option>
                    <option value="240">4 horas</option>
                    <option value="480">8 horas</option>
                    <option value="1440">24 horas</option>
                  </select>
                </div>

                <div>
                  <Label className="text-brand-ivory/80 text-sm">Redirecionar para</Label>
                  <select
                    value={timeoutNodeId}
                    onChange={(e) => setTimeoutNodeId(e.target.value)}
                    className="w-full mt-1 bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory rounded px-3 py-2 text-sm"
                  >
                    <option value="">Cancelar fluxo (padrão)</option>
                    {availableNodes.map((node) => (
                      <option key={node.id} value={node.id}>
                        {node.data?.label || node.type} ({node.type})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <p className="text-xs text-brand-ivory/40 mt-2">
                Se o cliente não responder no tempo configurado, o fluxo será redirecionado para o nó selecionado ou cancelado.
              </p>
            </div>

            <div className="mt-4 p-3 bg-brand-gold/5 rounded border border-brand-gold/20">
              <p className="text-xs text-brand-ivory/60 mb-2 font-medium">
                Funcionamento:
              </p>
              <ul className="text-xs text-brand-ivory/50 space-y-1 list-disc list-inside">
                <li>O sistema busca horários livres na agenda do usuário selecionado</li>
                <li>Retorna 3 opções de agendamento em horários fechados (00 ou 30 minutos)</li>
                <li>Se não encontrar no período inicial, busca o dobro de dias</li>
                <li>Respeita os horários mínimo e máximo configurados</li>
                <li>Pode incluir ou excluir finais de semana da busca</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setIsEditing(false)}
              className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
            >
              Cancelar
            </Button>
            <Button
              variant="premium"
              onClick={handleSave}
              disabled={!scheduleUserId}
              className="bg-purple-500 hover:bg-purple-600"
            >
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

ScheduleCheckNode.displayName = 'ScheduleCheckNode';
